/**
 * Shared floating video overlay (#2233).
 *
 * Moved out of `caption-editor/split-view/`. Both the transcript and
 * caption editors mount their own media player element (the video/audio
 * surface, not the transport controls) inside this overlay. The
 * overlay owns:
 *   - Drag position (mouse + keyboard nudge).
 *   - Show/hide state, restore from the "Video" chip.
 *   - Position persistence per session — the same session brings the
 *     window back to where the user left it, per the ticket
 *     acceptance list.
 *
 * The transport bar (play / skip / speed / timecode) lives at the
 * bottom of the editor — this component does not render it.
 *
 * WCAG 2.5.7: single-pointer / keyboard equivalents for the drag are
 * exposed on the handle (Arrow / Shift+Arrow / Home).
 */

import { ReactNode, KeyboardEvent, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { Maximize2, Minimize2, X } from 'lucide-react';
import cn from 'classnames';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';

const NUDGE_PX = 16;
const NUDGE_PX_LARGE = 64;
const STORAGE_PREFIX = 'scriptix_editor_floating_video';

/**
 * Minimum pixels of the overlay that must stay inside the viewport.
 * The drag handle itself is ~24px tall; this leaves a visible grab
 * target even if the user drags right up against an edge, so the
 * overlay can never trap itself off-screen (reported by a user who
 * lost access to Export because the overlay covered it and the
 * drag strip had moved out of the window).
 */
const MIN_VISIBLE_PX = 48;

interface FloatingVideoOverlayProps {
  /**
   * Whether the session has a video surface to render. Audio-only
   * sessions render nothing (per ticket: "Audio-only file: no empty
   * video box").
   */
  hasVideo: boolean;
  /**
   * Stable id to key position persistence — usually the session id.
   * Omit to disable persistence.
   */
  sessionId?: string | null;
  /** The media element to render inside the overlay. */
  children: ReactNode;
  /** Optional custom label shown on the drag handle. */
  label?: string;
  /**
   * Where the overlay starts the FIRST time the user opens it (no
   * saved position yet). Split view uses `bottom-left` so the video
   * doesn't land on top of the translation pane — the side being
   * edited — per the #2233 reopen comment "the floating video covers
   * the translation text, which is the pane being edited". Any drag
   * still persists and wins on subsequent visits.
   */
  defaultCorner?: 'bottom-right' | 'bottom-left';
}

interface Position {
  x: number;
  y: number;
}

function readPosition(sessionId: string | null | undefined): Position | null {
  if (!sessionId) return null;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}:${sessionId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.x !== 'number' ||
      typeof parsed?.y !== 'number' ||
      !Number.isFinite(parsed.x) ||
      !Number.isFinite(parsed.y)
    ) {
      return null;
    }
    return { x: parsed.x, y: parsed.y };
  } catch {
    return null;
  }
}

function writePosition(
  sessionId: string | null | undefined,
  position: Position
): void {
  if (!sessionId) return;
  try {
    window.localStorage.setItem(
      `${STORAGE_PREFIX}:${sessionId}`,
      JSON.stringify(position)
    );
  } catch {
    /* noop */
  }
}

/**
 * Keep at least ``MIN_VISIBLE_PX`` of the overlay inside the viewport
 * so the drag handle is always reachable. Base corner offsets match the
 * ``bottom-32 right-4`` Tailwind classes below; the drag transform sits
 * on top of those, so a negative ``y`` pushes the overlay up and a
 * positive ``y`` pushes it down. First paint has no ref, so we fall
 * back to generous bounds — the user hasn't moved it yet anyway.
 */
function clampPosition(
  position: Position,
  overlayEl: HTMLElement | null
): Position {
  if (typeof window === 'undefined') return position;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const rect = overlayEl?.getBoundingClientRect();
  const baseBottom = 128;
  const baseRight = 16;
  // ``||`` not ``??``: jsdom returns ``{width:0, height:0}`` for
  // elements that haven't actually rendered, which would collapse the
  // bounds to zero and clamp every nudge back to origin.
  const width = rect?.width || 400;
  const height = rect?.height || 300;
  const maxY = Math.max(0, baseBottom + height - MIN_VISIBLE_PX);
  const minY = Math.min(0, -(vh - baseBottom - height));
  const maxX = Math.max(0, baseRight + width - MIN_VISIBLE_PX);
  const minX = Math.min(0, -(vw - baseRight - width));
  return {
    x: Math.max(minX, Math.min(maxX, position.x)),
    y: Math.max(minY, Math.min(maxY, position.y))
  };
}

export function FloatingVideoOverlay({
  hasVideo,
  sessionId,
  children,
  label,
  defaultCorner = 'bottom-right'
}: FloatingVideoOverlayProps) {
  const { t } = useTranslation();
  const { cls } = useThemeVars();
  const dragRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState<boolean>(true);
  const [position, setPosition] = useState<Position>(
    () => readPosition(sessionId) ?? { x: 0, y: 0 }
  );

  // Persist any position change (drag / keyboard nudge).
  useEffect(() => {
    writePosition(sessionId, position);
  }, [sessionId, position]);

  // Recover an off-screen position: on first mount (after the ref
  // resolves) and whenever the window is resized. This handles both
  // the "stored position is already off-screen" case (user hit the
  // bug once, reloaded, overlay still unreachable) and the
  // "window got smaller" case.
  useEffect(() => {
    const recover = () => {
      setPosition(p => {
        const clamped = clampPosition(p, dragRef.current);
        if (clamped.x === p.x && clamped.y === p.y) return p;
        return clamped;
      });
    };
    recover();
    window.addEventListener('resize', recover);
    return () => window.removeEventListener('resize', recover);
  }, []);

  if (!hasVideo) return null;

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? NUDGE_PX_LARGE : NUDGE_PX;
    const nudge = (dx: number, dy: number) =>
      setPosition(p =>
        clampPosition({ x: p.x + dx, y: p.y + dy }, dragRef.current)
      );
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        nudge(0, -step);
        return;
      case 'ArrowDown':
        e.preventDefault();
        nudge(0, step);
        return;
      case 'ArrowLeft':
        e.preventDefault();
        nudge(-step, 0);
        return;
      case 'ArrowRight':
        e.preventDefault();
        nudge(step, 0);
        return;
      case 'Home':
        e.preventDefault();
        setPosition({ x: 0, y: 0 });
        return;
    }
  };

  const restoreLabel = label ?? t('video-overlay-restore') ?? 'Video';

  // Base-corner CSS: the drag transform is applied on top of this, so
  // flipping to the left corner just shifts the "home" position.
  const cornerBase =
    defaultCorner === 'bottom-left'
      ? { restore: 'bottom-20 left-4', dragged: 'bottom-32 left-4' }
      : { restore: 'bottom-20 right-4', dragged: 'bottom-32 right-4' };

  if (!visible) {
    return (
      <button
        onClick={() => setVisible(true)}
        className={cn(
          'fixed z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-2xl shadow-lg text-xs font-medium border',
          cornerBase.restore,
          cls.border,
          cls.cardBg,
          cls.text
        )}
        aria-label={restoreLabel}
      >
        <Maximize2 size={12} aria-hidden="true" />
        {restoreLabel}
      </button>
    );
  }

  return (
    <Draggable
      nodeRef={dragRef}
      handle=".drag-handle"
      position={position}
      onStop={(_e, data) =>
        setPosition(clampPosition({ x: data.x, y: data.y }, dragRef.current))
      }
    >
      <div
        ref={dragRef}
        className={cn(
          'fixed z-50 rounded-2xl shadow-2xl border overflow-hidden',
          cornerBase.dragged,
          cls.border
        )}
        style={{ width: 400, maxWidth: '90vw' }}
      >
        <div
          role="application"
          tabIndex={0}
          aria-label={
            t('video-overlay-drag-handle-label') ||
            'Video overlay. Use arrow keys to move, Shift+Arrow for larger steps, Home to reset.'
          }
          onKeyDown={handleKeyDown}
          className={cn(
            'drag-handle flex items-center justify-between px-2 py-1 cursor-move focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            cls.cardBg,
            cls.text
          )}
        >
          <span className="text-[11px] font-medium select-none opacity-60">
            {label ?? t('video-overlay-title') ?? 'Video'}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setVisible(false)}
              className="p-1 rounded-2xl hover:opacity-70 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={t('minimize') || 'Minimize'}
            >
              <Minimize2 size={12} aria-hidden="true" />
            </button>
            <button
              onClick={() => setVisible(false)}
              className="p-1 rounded-2xl hover:opacity-70 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={t('close') || 'Close'}
            >
              <X size={12} aria-hidden="true" />
            </button>
          </div>
        </div>

        {children}
      </div>
    </Draggable>
  );
}
